/* Karaoke Studio — karaoke video export.
 *
 * Renders the backing track + animated, line/word-highlighted lyrics to a
 * canvas and encodes it to MP4. Two lanes:
 *
 *   FAST (default) — the shared WebCodecs primitive, public/js/canvas-video-
 *     export.js (`CanvasVideoExport`), the same one /audio-to-video,
 *     /slideshow-maker, /music-video-maker and /split-screen use. Frame-by-frame
 *     and offline: it asks drawFrame for frame N and encodes it, as fast as the
 *     machine allows.
 *
 *   SLOW (fallback) — the original real-time MediaRecorder capture → FFmpeg-WASM
 *     VP9→H.264 transcode, for browsers with no WebCodecs MP4 support. Gated
 *     behind CanvasVideoSlowPath so nobody is dropped into it unknowingly, and
 *     watchdogged so it can no longer hang forever.
 *
 * WHY THE FAST LANE EXISTS HERE — measured on this file, 2026-08-10, from
 * feedback #1339 (вова, «ПАРАША» after an hour on /karaoke). Telemetry: 140
 * sessions started a video export in 60 days, 31 finished, ONE errored. The
 * other 108 vanished silently. Two independent causes, both proven:
 *
 *   1. SPEED. The capture is real-time by construction — a 3-minute song takes
 *      3 minutes to record before FFmpeg even starts. Same 30 s of content,
 *      same browser: legacy 161 793 ms (5.39× realtime) vs WebCodecs 2 060 ms
 *      (0.07×). Extrapolated to a 188 s song that is ~17 minutes against ~13
 *      seconds. /karaoke's audience is 66 % Yandex Browser, which HAS WebCodecs
 *      (935 ok vs 107 unsupported on the control tool) — it was simply never
 *      offered the lane.
 *
 *   2. A SILENT UNBOUNDED HANG. The capture loop ends only when
 *      `actx.currentTime - t0 >= wallDuration`. Chrome suspends an AudioContext
 *      in a backgrounded tab (and on a mobile app switch), which FREEZES
 *      currentTime — so the promise never settles, with no timeout, no
 *      rejection and no telemetry. Measured: a 10 s clip whose context was
 *      suspended 3 s in was still pending 30 s later. That is the shape of the
 *      108 missing sessions, and it is why the fallback now carries a watchdog.
 *
 * Also fixed by moving off real-time capture: `fps: 25` was ignored — the RAF
 * draw loop let captureStream emit ~60 fps, so ffprobe counted 1 209 video
 * packets in a 20 s file instead of 500, and FFmpeg then had 2.4× the frames to
 * chew through. The frame-by-frame lane emits exactly durationSec × fps.
 *
 * Key and tempo are NOT applied here any more. The caller hands over a buffer
 * that already has them baked in (karaoke-app's ensureProcessedBuffer, via the
 * shared AudioPipeline.stretch), because the old approach — playbackRate=tempo
 * plus a compensating detune — cancelled itself out to a no-op while every
 * consumer still scaled by tempo. See the comment block in karaoke-app.js.
 */
(function () {
    'use strict';

    var RESOLUTIONS = {
        720:  { w: 1280, h: 720,  vbps: 4500000 },
        1080: { w: 1920, h: 1080, vbps: 8000000 },
    };

    function pickWebmMime() {
        var candidates = [
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp9',
            'video/webm;codecs=vp8,opus',
            'video/webm;codecs=vp8',
            'video/webm',
        ];
        for (var i = 0; i < candidates.length; i++) {
            try {
                if (window.MediaRecorder && MediaRecorder.isTypeSupported(candidates[i])) return candidates[i];
            } catch (_) {}
        }
        return null;
    }

    function isAvailable() {
        return typeof window.MediaRecorder !== 'undefined'
            && pickWebmMime() != null
            && typeof document.createElement('canvas').captureStream === 'function';
    }

    function fmtTime(s) {
        if (!s || isNaN(s) || s < 0) s = 0;
        var m = Math.floor(s / 60), sec = Math.floor(s % 60);
        return m + ':' + (sec < 10 ? '0' : '') + sec;
    }

    // ── Frame renderer ──────────────────────────────────────────────────────
    // Draws background + a centered block of lyric lines (active line large +
    // word-wipe), plus a progress bar. Pure function of song time.
    function drawFrame(ctx, W, H, opts) {
        var timeline = opts.timeline || [];
        var t = opts.timeS;
        var rtl = !!opts.rtl;

        // Background
        if (opts.bgStyle === 'gradient') {
            var g = ctx.createLinearGradient(0, 0, W, H);
            g.addColorStop(0, '#0b1220');
            g.addColorStop(1, '#1e1b4b');
            ctx.fillStyle = g;
        } else {
            ctx.fillStyle = '#0b1220';
        }
        ctx.fillRect(0, 0, W, H);

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.direction = rtl ? 'rtl' : 'ltr';
        var cx = W / 2;
        var cy = H / 2;
        var fontStack = 'Inter, "Segoe UI", system-ui, Arial, sans-serif';

        if (!timeline.length) {
            ctx.fillStyle = 'rgba(255,255,255,0.35)';
            ctx.font = Math.round(H * 0.045) + 'px ' + fontStack;
            ctx.fillText(opts.emptyText || '', cx, cy);
        } else if (window.LyricsCanvas) {
            // The lyric block itself is the SHARED painter (public/js/lyrics-canvas.js),
            // so /karaoke and /audio-to-video cannot drift on word-wipe boundaries,
            // RTL word order or what happens between two lines. This file keeps only
            // what is karaoke's own: the background, the progress bar and the timecode.
            // Wrapping is ON here — the old inline renderer drew one unwrapped
            // fillText per line, which ran off the edge on a portrait frame.
            window.LyricsCanvas.draw(ctx, W, H, timeline, t, {
                mode: 'stack',
                neighbors: 2,
                rtl: rtl,
                font: fontStack,
                activeSize: Math.round(H * 0.072),
                inactiveSize: Math.round(H * 0.05),
                lineGap: Math.round(H * 0.11),
                activeColor: '#38bdf8',
                pastColor: 'rgba(255,255,255,0.28)',
                upcomingColor: 'rgba(255,255,255,0.5)',
                unsungColor: 'rgba(255,255,255,0.35)',
                wordWipe: true,
                wipeStyle: 'word',
            });
        } else {
            // Fallback for a stale Service-Worker cache that has this file but not
            // yet lyrics-canvas.js: active line only, unwrapped. Degraded, never blank.
            var idx = window.KaraokeLyrics ? window.KaraokeLyrics.activeIndexAt(timeline, t) : -1;
            if (idx >= 0) {
                ctx.font = '800 ' + Math.round(H * 0.072) + 'px ' + fontStack;
                ctx.fillStyle = '#38bdf8';
                ctx.fillText(timeline[idx].text, cx, cy);
            }
        }

        // Progress bar
        var dur = opts.durationS || 0;
        var pct = dur > 0 ? Math.max(0, Math.min(1, t / dur)) : 0;
        var barH = Math.max(4, Math.round(H * 0.008));
        var barY = H - barH * 4;
        var barW = W * 0.8;
        var barX = (W - barW) / 2;
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fillRect(barX, barY, barW, barH);
        ctx.fillStyle = '#38bdf8';
        ctx.fillRect(rtl ? barX + barW * (1 - pct) : barX, barY, barW * pct, barH);

        // Timecode
        ctx.font = '600 ' + Math.round(H * 0.026) + 'px ' + fontStack;
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.textAlign = 'center';
        ctx.fillText(fmtTime(t) + ' / ' + fmtTime(dur), cx, barY - H * 0.035);
    }

    // (The word-wipe used to live here. It is now LyricsCanvas.drawRow /
    // drawRowSmooth in public/js/lyrics-canvas.js — one painter for every tool.)

    // ── WebM capture ────────────────────────────────────────────────────────
    async function captureWebM(opts, onProgress, signal) {
        if (!isAvailable()) {
            var err = new Error('mediarecorder-unavailable');
            err.code = 'MEDIARECORDER_UNAVAILABLE';
            throw err;
        }
        var buffer = opts.audioBuffer;
        if (!buffer) throw new Error('no-audio-buffer');

        var res = RESOLUTIONS[opts.resolution] || RESOLUTIONS[720];
        var fps = Math.max(15, Math.min(30, opts.fps || 25));
        /* The buffer arrives with key and tempo already applied, so it is played
           back untouched at 1.0×. This lane used to set playbackRate=tempo plus
           a compensating detune, which per spec multiply into
           2^(key/12) — a no-op for tempo — while `wallDuration` still divided by
           tempo and truncated the film to 67 % at 150 %. Not reintroducible:
           there is no rate knob here any more. */
        var tempo = 1;

        var canvas = document.createElement('canvas');
        canvas.width = res.w;
        canvas.height = res.h;
        var ctx2d = canvas.getContext('2d');

        // The first video recorder of a Chromium tab drops its first ~2 s of
        // frames: the lyrics would stand still while the song runs on.
        if (window.__mediaRecorderWarm) await window.__mediaRecorderWarm({ width: res.w, height: res.h, mimeType: pickWebmMime(), videoBitsPerSecond: res.vbps });

        var Ctor = window.AudioContext || window.webkitAudioContext;
        var actx = new Ctor();
        if (actx.state === 'suspended') { try { await actx.resume(); } catch (_) {} }

        var src = actx.createBufferSource();
        src.buffer = buffer;   // 1.0× — key/tempo are already in the buffer
        var dest = actx.createMediaStreamDestination();
        src.connect(dest);

        var stream = canvas.captureStream(fps);
        dest.stream.getAudioTracks().forEach(function (tr) { stream.addTrack(tr); });

        var recorder = new MediaRecorder(stream, {
            mimeType: pickWebmMime(),
            videoBitsPerSecond: res.vbps,
            audioBitsPerSecond: 192000,
        });
        var chunks = [];
        recorder.ondataavailable = function (e) {
            if (e.data && e.data.size > 0) chunks.push(e.data);
        };
        var stopPromise = new Promise(function (resolve) { recorder.onstop = resolve; });

        var wallDuration = buffer.duration / tempo;     // real seconds of recording
        var aborted = false;
        var abortListener = null;
        if (signal) {
            if (signal.aborted) aborted = true;
            else { abortListener = function () { aborted = true; }; signal.addEventListener('abort', abortListener); }
        }

        var frameOpts = {
            timeline: opts.timeline || [],
            durationS: buffer.duration,
            bgStyle: opts.bgStyle || 'gradient',
            rtl: !!opts.rtl,
            emptyText: opts.emptyText || '',
            timeS: 0,
        };

        recorder.start(250);
        var t0 = actx.currentTime;
        src.start(0);

        var lastReport = 0;
        var settledOk = false;
        /* WATCHDOG. This loop's only exit is the audio clock passing
           wallDuration, and that clock stops dead when the browser suspends the
           AudioContext — a backgrounded tab, a mobile app switch, the screen
           locking. Measured 2026-08-10: a 10 s clip whose context was suspended
           3 s in was still pending 30 s later, silently. Recording is real-time,
           so honest progress is ~1 s of clock per 1 s of wall time; if the clock
           has not moved for STALL_MS while we are not aborting, the run is dead
           and must SAY so rather than hang until the user gives up (108 of 140
           export sessions in 60 days ended exactly that way).

           Generous on purpose: a slow machine still advances the audio clock,
           so this only fires when it has genuinely stopped. */
        var STALL_MS = 15000;
        try {
            await new Promise(function (resolve, reject) {
                var lastClock = -1, lastClockMoveAt = performance.now();
                function frame() {
                    if (aborted) { resolve(); return; }
                    var clock = actx.currentTime - t0;
                    var now = performance.now();
                    if (clock > lastClock + 0.001) { lastClock = clock; lastClockMoveAt = now; }
                    else if (now - lastClockMoveAt > STALL_MS) {
                        var err = new Error('capture-stalled');
                        err.code = 'CAPTURE_STALLED';
                        err.ctxState = actx.state;
                        err.atSec = Math.round(clock * 10) / 10;
                        reject(err);
                        return;
                    }
                    // Song position from the audio clock — no RAF drift.
                    var songT = clock * tempo;
                    frameOpts.timeS = Math.min(songT, buffer.duration);
                    drawFrame(ctx2d, res.w, res.h, frameOpts);
                    if (onProgress && now - lastReport > 120) {
                        onProgress(0.05 + 0.8 * Math.min(1, songT / Math.max(0.01, buffer.duration)), 'recording');
                        lastReport = now;
                    }
                    if (clock >= wallDuration) { resolve(); return; }
                    requestAnimationFrame(frame);
                }
                requestAnimationFrame(frame);
            });
            settledOk = true;
        } finally {
            if (abortListener && signal) signal.removeEventListener('abort', abortListener);
            // Runs on the watchdog path too, so a stalled export does not leave
            // a live recorder and AudioContext behind.
            try { src.stop(); } catch (_) {}
            try { if (recorder.state !== 'inactive') recorder.stop(); } catch (_) {}
            if (!settledOk) { try { actx.close(); } catch (_) {} }
        }

        await stopPromise;
        try { actx.close(); } catch (_) {}

        if (aborted) throw new Error('aborted');
        if (onProgress) onProgress(0.88, 'finalizing');
        return new Blob(chunks, { type: 'video/webm' });
    }

    async function exportWebm(opts, onProgress, signal) {
        var blob = await captureWebM(opts, onProgress, signal);
        if (onProgress) onProgress(1, 'done');
        return blob;
    }

    /* ── FAST lane: the shared WebCodecs primitive ───────────────────────────
       drawFrame is already a pure function of song time, which is exactly the
       contract CanvasVideoExport.encode wants — so there is no second renderer
       here, just the same painter driven by frame index instead of by an audio
       clock. That is also what removes every way this lane could hang: no
       real-time wait, no AudioContext, no RAF. */
    function resolutionOf(opts) {
        return RESOLUTIONS[opts.resolution] || RESOLUTIONS[720];
    }

    async function probeFastLane(opts) {
        var CVE = window.CanvasVideoExport;
        if (!CVE || typeof CVE.isSupported !== 'function') return { ok: false, reason: 'no-primitive' };
        var res = resolutionOf(opts);
        var buf = opts.audioBuffer;
        try {
            return await CVE.isSupported({
                width: res.w, height: res.h, fps: Math.max(15, Math.min(30, opts.fps || 25)),
                format: opts.format === 'webm' ? 'webm' : 'mp4',
                audioSampleRate: buf ? buf.sampleRate : 44100,
                audioChannels: buf ? buf.numberOfChannels : 2,
            });
        } catch (e) {
            return { ok: false, reason: 'probe-threw' };
        }
    }

    async function exportViaWebCodecs(opts, onProgress, signal) {
        var CVE = window.CanvasVideoExport;
        var res = resolutionOf(opts);
        var buffer = opts.audioBuffer;
        if (!buffer) throw new Error('no-audio-buffer');
        var fps = Math.max(15, Math.min(30, opts.fps || 25));
        var frameOpts = {
            timeline: opts.timeline || [],
            durationS: buffer.duration,
            bgStyle: opts.bgStyle || 'gradient',
            rtl: !!opts.rtl,
            emptyText: opts.emptyText || '',
            timeS: 0,
        };
        return await CVE.encode({
            width: res.w, height: res.h, fps: fps,
            durationSec: buffer.duration,
            format: opts.format === 'webm' ? 'webm' : 'mp4',
            qualityCrf: 23,
            audioBuffer: buffer,
            signal: signal,
            onProgress: function (r) { if (onProgress) onProgress(r, 'rendering'); },
            drawFrame: function (ctx2d, frameIndex, timeSec) {
                frameOpts.timeS = Math.min(timeSec, buffer.duration);
                drawFrame(ctx2d, res.w, res.h, frameOpts);
            },
        });
    }

    /* ── MP4 ──────────────────────────────────────────────────────────────────
       Fast lane when the browser can encode H.264 through WebCodecs, otherwise
       the real-time capture + FFmpeg transcode. Callers that want to warn the
       user before committing to the slow one probe first with probeFastLane()
       and gate on CanvasVideoSlowPath — see karaoke-app.js. `forceLane` exists
       for that gate's "keep MP4 anyway" answer, and for tests. */
    async function exportMp4(opts, onProgress, signal) {
        opts = opts || {};
        if (opts.forceLane !== 'recorder') {
            var sup = await probeFastLane(opts);
            if (sup.ok) {
                try {
                    var fast = await exportViaWebCodecs(opts, onProgress, signal);
                    if (onProgress) onProgress(1, 'done');
                    report('video_export_lane', { lane: 'webcodecs', res: opts.resolution, hw: sup.hardware ? 1 : 0 });
                    return fast;
                } catch (e) {
                    // A genuine cancel is not a lane failure — let it through.
                    if (isAbort(e) || (signal && signal.aborted)) throw e;
                    report('video_export_lane_fallback', {
                        lane: 'webcodecs', res: opts.resolution,
                        msg: String(e && e.message).slice(0, 100),
                    });
                    /* Do NOT quietly continue into the recorder lane. The gate
                       above exists precisely so nobody is committed to a 5–15
                       minute real-time capture without being asked, and falling
                       through here would smuggle them into it after the fast
                       lane failed. Hand the decision back to the caller with a
                       flag saying a retry IS possible. */
                    var fe = new Error('fast-lane-failed:' + String(e && e.message).slice(0, 80));
                    fe.code = 'FAST_LANE_FAILED';
                    fe.canRetryWithRecorder = KVE_isAvailable();
                    fe.cause = e;
                    throw fe;
                }
            } else if (opts.forceLane !== 'webcodecs') {
                report('video_export_lane', { lane: 'recorder', res: opts.resolution, reason: sup.reason });
            } else {
                var err = new Error('webcodecs-unavailable:' + sup.reason);
                err.code = 'WEBCODECS_UNAVAILABLE';
                throw err;
            }
        }
        return await exportMp4ViaRecorder(opts, onProgress, signal);
    }

    function isAbort(e) {
        return !!e && (e.name === 'AbortError' || String(e.message) === 'aborted');
    }

    /** Can the recorder lane run at all here? (named apart from isAvailable so
        the hoisted reference inside exportMp4 stays obvious) */
    function KVE_isAvailable() { return isAvailable(); }

    function report(f, payload) {
        try {
            if (window.__ae) window.__ae('feature_use', Object.assign({ slug: 'karaoke', f: f }, payload || {}));
        } catch (_) {}
    }

    // ── MP4 fallback (capture WebM in real time → transcode H.264+AAC) ───────
    async function exportMp4ViaRecorder(opts, onProgress, signal) {
        var webm = await captureWebM(opts, function (r, label) {
            if (onProgress) onProgress(r * 0.6, label || 'recording');
        }, signal);
        if (signal && signal.aborted) throw new Error('aborted');

        if (!window.FFmpegProcessor) throw new Error('ffmpeg-not-loaded');
        var ready = await window.FFmpegProcessor.init();
        if (!ready) throw new Error('ffmpeg-init-failed');

        if (typeof window.FFmpegProcessor.onProgress === 'function') {
            window.FFmpegProcessor.onProgress(function (p) {
                if (onProgress) onProgress(0.6 + 0.38 * Math.max(0, Math.min(1, p)), 'transcoding');
            });
        }
        // Never `-c copy` — MP4 can't reliably carry VP9/Opus (Safari).
        /* ⚠️ `-r` ОБЯЗАТЕЛЕН на выходе. Захват MediaRecorder'ом идёт с
           миллисекундной шкалой и без объявленной частоты кадров, и FFmpeg
           принимает шкалу за частоту. Замерено на /slideshow-maker 04.09.2026,
           на том же виде входа: девятисекундная запись давала
           `frame=7877 dup=7640 speed=0.0353x` — тридцатикратное дублирование
           и четыре с лишним минуты вместо секунд. Здесь этот лейн сейчас почти
           не используется (за 30 дней все 143 размеченные сессии ушли на
           WebCodecs), но когда он сработает — сработает так же. */
        var args = [
            '-c:v', 'libx264', '-preset', 'fast', '-crf', '22', '-pix_fmt', 'yuv420p',
            '-r', String(Math.max(15, Math.min(30, (opts && opts.fps) || 25))),
            '-c:a', 'aac', '-b:a', '192k',
            '-movflags', '+faststart',
        ];
        var result = await window.FFmpegProcessor.processStreaming(webm, args, 'output.mp4', 'input.webm');
        if (signal && signal.aborted) throw new Error('aborted');
        if (onProgress) onProgress(1, 'done');
        // processStreaming hands back application/octet-stream; retype it so the
        // saved file and any in-page <video> get a real MP4 to work with.
        var out = result.blob;
        if (out && out.type !== 'video/mp4') out = new Blob([out], { type: 'video/mp4' });
        return out;
    }

    window.KaraokeVideoExport = {
        isAvailable: isAvailable,
        probeFastLane: probeFastLane,             // caller gates the slow lane on this
        exportWebm: exportWebm,
        exportMp4: exportMp4,
        exportMp4ViaRecorder: exportMp4ViaRecorder,   // exported for tests
        drawFrame: drawFrame,   // exported for unit tests
    };
})();
